import axios, {
  CanceledError,
  type AxiosInstance,
  type GenericAbortSignal,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  calculateBackoffDelayMs,
  localeHeader,
  parseRetryAfter,
  readProblem,
} from '@blpde/connect/http';
import { getApiOrigin, tryShellRefresh } from '@blpde/connect/auth';
import { attachRecordLockClientHeader } from './recordLockClient';

declare global {
  interface Window {
    __API_URL__?: string;
  }
}

type PaginatedApiResponse<ResourceName extends string, ResourceData extends object> = {
  [_ in ResourceName]: ResourceData[];
} & {
  metadata: {
    count: number;
    nextURL?: string;
    prevURL?: string;
    currentURL: string;
    pages: number;
    currentPage: number;
  };
};

// API origin resolution is shared with the Connect shell and every sub-app:
// one implementation in @blpde/connect (reads `?url=`, the stored
// `formica.api.baseUrl`, `window.__API_URL__`, then `VITE_API_URL`).
export const baseURL = `${getApiOrigin()}/v1`;

// Auth credential storage key. Written by the auth store on login (and by
// the SSO `?token=` handoff in the router guard). Read here directly rather
// than through the store to avoid an import cycle (store → composables → api).
// The bearer is the only credential: a session UUID never authenticates.
export const TOKEN_KEY = 'nx_token';

/** Attaches the stored bearer and the UI language to every outgoing request. */
function attachAuth(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  config.headers.set('Accept-Language', localeHeader()['Accept-Language']);
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
}

/** Attaches ERP request metadata shared by REST and GraphQL HTTP fallback. */
function attachErpRequestMeta(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  attachAuth(config);
  return attachRecordLockClientHeader(config);
}

type RetriableConfig = InternalAxiosRequestConfig & { _formicaRetried?: boolean };

/**
 * On 401 ask the Connect shell to refresh the bearer once (the same
 * single-in-flight refresh every other app uses), then replay the request —
 * the request interceptor re-attaches the fresh token. If a refresh isn't
 * possible the stale credential is dropped so the next boot lands on /login.
 * The `_formicaRetried` flag bounds it to one retry, so no refresh loop.
 */
function make401Handler(client: AxiosInstance) {
  return async (error: unknown): Promise<unknown> => {
    const err = error as {
      response?: { status?: number };
      config?: RetriableConfig;
    } | null;
    // The terminal's own session 401 (#2099 point 2): the OPERATOR's NX login is
    // fine, only the employee session at the terminal ended. Refreshing the
    // bearer would not help, and dropping `nx_token` would log the operator out.
    if (err?.response?.status === 401 && readProblem(error)?.code === 'terminal_session_invalid') {
      return Promise.reject(error);
    }
    const config = err?.config;
    if (err?.response?.status !== 401 || !config || config._formicaRetried) {
      if (err?.response?.status === 401) localStorage.removeItem(TOKEN_KEY);
      return Promise.reject(error);
    }
    const refreshed = await tryShellRefresh();
    if (!refreshed) {
      localStorage.removeItem(TOKEN_KEY);
      return Promise.reject(error);
    }
    config._formicaRetried = true;
    return client.request(config);
  };
}

/** Retries a failed request at most this often (503 / 502 / 504 only). */
const MAX_TRANSIENT_RETRIES = 3;
/** Methods that may be sent again on 502 / 503 / 504 — sending them twice does no harm. */
const IDEMPOTENT_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);
/**
 * The codes of an erp 503 that mean "nothing was written, try again"
 * (rule 16): a collision rolled back, the database or the module could not be
 * reached before anything was written. An allow-list: the only codes a write
 * is sent again on.
 */
const TRANSIENT_503_CODES: ReadonlySet<string> = new Set([
  'transaction_conflict',
  'database_unavailable',
  'upstream_unreachable',
  // a statement timeout before COMMIT: nothing was written (rule 16)
  'timeout',
]);
/**
 * The codes of a 503 that stays the same however often it is asked — a
 * deny-list: a read is not sent again on them (without `Retry-After`), so the
 * error shows at once instead of after the backoff. Every other 503 code is
 * repeated on a read.
 */
const DETERMINISTIC_503_CODES: ReadonlySet<string> = new Set([
  // master's gateway: no instance of the module is connected (failed boot, not started)
  'no_clients_connected',
  // erp: no file storage backend is configured — a setup gap, not a passing state
  'storage_unavailable',
]);

/**
 * Whether a write (POST, PATCH, PUT, DELETE) may be sent again on a 503 — a
 * property of the backend a client talks to, so every client states it:
 *
 * - `'transient-503'`: only erp. Its backend classifies and marks the COMMIT
 *   phase and answers 503 only when nothing was written (rule 16, K25). A
 *   write is sent again when the 503 names a transient condition — a code of
 *   TRANSIENT_503_CODES or a `Retry-After`; a deterministic 503 (e.g.
 *   "storage backend not configured") is not, it would only fail again.
 * - `'never'`: every other backend (master, print). Its 503 promises nothing
 *   — print saves `POST /jobs/render` in autocommit, so a second send could
 *   store the job twice.
 */
type WriteRetryPolicy = 'transient-503' | 'never';

interface TransientRetryOptions {
  writes: WriteRetryPolicy;
}

type TransientRetryConfig = InternalAxiosRequestConfig & { _transientRetries?: number };

type FailedRequest = {
  response?: {
    status?: number;
    headers?: { get?: (name: string) => unknown } | Record<string, unknown>;
  };
  config?: TransientRetryConfig;
} | null;

/** The response's Retry-After value, or null when it carries none. */
function retryAfterHeader(err: FailedRequest): string | null {
  const headers = err?.response?.headers;
  const raw =
    typeof headers?.get === 'function'
      ? headers.get('retry-after')
      : (headers as Record<string, unknown> | undefined)?.['retry-after'];
  if (raw == null) return null;
  const value = String(raw).trim();
  return value === '' ? null : value;
}

/**
 * Whether a failed request may be sent again, by method, status and the
 * client's policy:
 *
 * - A read (GET, HEAD, OPTIONS): on 502 / 503 / 504, except a 503 with a code
 *   of DETERMINISTIC_503_CODES and no `Retry-After` — that refusal would only
 *   come again and delay the error page. Any other 503 (no code, a transient
 *   or an unknown code, `module_client_closed`, `master_maintenance`, …) is
 *   repeated.
 * - A write: only on a 503 with `Retry-After` or a code of
 *   TRANSIENT_503_CODES, and only where the policy allows it
 *   (`'transient-503'`, erp). Never on a 502 / 504.
 */
function isTransientFailure(error: unknown, method: string, writes: WriteRetryPolicy): boolean {
  const err = error as FailedRequest;
  const status = err?.response?.status;
  if (status !== 502 && status !== 503 && status !== 504) return false;
  const read = IDEMPOTENT_METHODS.has(method);
  if (status !== 503) return read;
  const retryAfter = retryAfterHeader(err) !== null;
  const code = readProblem(error)?.code ?? '';
  if (read) return retryAfter || !DETERMINISTIC_503_CODES.has(code);
  return writes === 'transient-503' && (retryAfter || TRANSIENT_503_CODES.has(code));
}

/** Waits ms; false when the signal aborts first (or already has), which ends the wait at once. */
function waitUnlessAborted(ms: number, signal: GenericAbortSignal | undefined): Promise<boolean> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve(false);
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort);
      resolve(true);
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      resolve(false);
    }
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

/** The error axios raises for an aborted request — what an abort during the backoff ends with. */
function canceledError(config: InternalAxiosRequestConfig): CanceledError<unknown> {
  // At runtime the constructor takes (message, config, request), its typing
  // AxiosError's (message, code, config, …): set the config afterwards.
  const error = new CanceledError<unknown>();
  error.config = config;
  return error;
}

/**
 * Sends the request again after a short backoff when the backend says it did
 * not (or may not) have carried it out:
 *
 * - Idempotent methods (GET, HEAD, OPTIONS), on every client: 502 / 503 /
 *   504, except a 503 with a known deterministic code (isTransientFailure).
 * - A write only on a 503, and only where the client's policy allows it
 *   (`writes: 'transient-503'`, erp): with a transient code or `Retry-After`.
 *   Never on a 502 / 504 — the gateway may answer them after the module
 *   already did the work.
 * - Never 500 (a bug that must show at once) or any other status.
 *
 * `Retry-After` wins over the backoff; at most MAX_TRANSIENT_RETRIES retries,
 * then the error goes on to the caller. An abort of the request's `signal`
 * during the backoff ends it at once with axios' CanceledError, as an abort
 * of the request itself does.
 */
export function makeTransientRetryHandler(
  client: AxiosInstance,
  { writes }: TransientRetryOptions
) {
  return async (error: unknown): Promise<unknown> => {
    const err = error as FailedRequest;
    const config = err?.config;
    if (!config || !err?.response?.status) return Promise.reject(error);
    const method = String(config.method ?? 'get').toUpperCase();
    const done = config._transientRetries ?? 0;
    if (!isTransientFailure(error, method, writes) || done >= MAX_TRANSIENT_RETRIES) {
      return Promise.reject(error);
    }
    config._transientRetries = done + 1;
    const delayMs = calculateBackoffDelayMs(done, parseRetryAfter(retryAfterHeader(err)));
    if (!(await waitUnlessAborted(delayMs, config.signal))) {
      return Promise.reject(canceledError(config));
    }
    return client.request(config);
  };
}

// Module key the master gateway routes the nx data API to. Sent as the
// `BLP-Module` header on every request; also usable as a `?module=` query
// param — the master honours that fallback (api/public/api.go), which is the
// only way to route a CORS preflight (OPTIONS), since the browser strips
// custom headers off the preflight but keeps query params.
// The backend registers under the master module key `erp` (master mux.go;
// the old `apinx` key is retired).
export const NX_MODULE = 'erp';

// nx data client — the master gateway routes it to the nx module via the
// `BLP-Module` header.
const apiClient = axios.create({
  baseURL,
  headers: { 'BLP-Module': NX_MODULE },
});
apiClient.interceptors.request.use(attachErpRequestMeta);
apiClient.interceptors.response.use((r) => r, make401Handler(apiClient));
// After the 401 handler (which stays first): a 503/502/504 passes it untouched.
// Backend erp: its 503 means nothing was written, so a write may go again on a transient one.
apiClient.interceptors.response.use(
  (r) => r,
  makeTransientRetryHandler(apiClient, { writes: 'transient-503' })
);

// master client — hits the master's OWN endpoints (/oauth/*, /session). It
// carries NO `BLP-Module` header, so the gateway serves it directly instead
// of proxying to a module.
const masterClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});
masterClient.interceptors.request.use(attachAuth);
masterClient.interceptors.response.use((r) => r, make401Handler(masterClient));
// After the 401 handler (which stays first): a 503/502/504 passes it untouched.
// Backend master: its 503 promises nothing about a write, so only reads go again.
masterClient.interceptors.response.use(
  (r) => r,
  makeTransientRetryHandler(masterClient, { writes: 'never' })
);

// print client — routes to the standalone `print` module (the local-printing
// broker). Same gateway pattern as apiClient, just a different BLP-Module key.
const printClient = axios.create({
  baseURL,
  headers: { 'BLP-Module': 'print' },
});
printClient.interceptors.request.use(attachAuth);
printClient.interceptors.response.use((r) => r, make401Handler(printClient));
// After the 401 handler (which stays first): a 503/502/504 passes it untouched.
// Backend print: it saves `POST /jobs/render` in autocommit, so only reads go again.
printClient.interceptors.response.use(
  (r) => r,
  makeTransientRetryHandler(printClient, { writes: 'never' })
);

export { apiClient, masterClient, printClient };
export type { PaginatedApiResponse };
